import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SEO, { seoConfigs } from '../../components/SEO';
import { technologies } from '../data/technologies';
import { ChevronRight, Server, ArrowRight } from 'lucide-react';
import Diagram from '../components/Diagram';

const Technologies = () => {
    const location = useLocation();

    // Initialize active state from URL param or default to first
    const getInitialId = () => {
        const params = new URLSearchParams(location.search);
        const urlId = params.get('id');
        if (urlId && technologies.some(t => t.id === urlId)) {
            return urlId;
        }
        return technologies[0].id;
    };

    const [activeId, setActiveId] = useState(getInitialId());

    // Update active state if URL param changes
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const urlId = params.get('id');
        if (urlId && technologies.some(t => t.id === urlId)) {
            setActiveId(urlId);
        }
    }, [location.search]);

    const activeTech = technologies.find(t => t.id === activeId) || technologies[0];
    const activeIndex = technologies.findIndex(t => t.id === activeId);
    const nextTech = activeIndex < technologies.length - 1 ? technologies[activeIndex + 1] : null;

    // Group technologies by category
    const groupedTechnologies = technologies.reduce((acc, curr) => {
        if (!acc[curr.category]) acc[curr.category] = [];
        acc[curr.category].push(curr);
        return acc;
    }, {});

    // Scroll to top when switching topics
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [activeId]);

    const handleNextTopic = () => {
        if (nextTech) setActiveId(nextTech.id);
    };

    return (
        <>
            <SEO
                title={`${activeTech.title} — Key Technologies for System Design | Rastor AI`}
                description={`Learn how to use ${activeTech.title} in distributed systems. ${seoConfigs.sysdesignTechnologies.description}`}
                keywords={`${activeTech.title}, ${seoConfigs.sysdesignTechnologies.keywords}`}
                canonicalUrl={`/sysdesign/technologies?id=${activeTech.id}`}
            />

            <div className="sd-container sd-fade-in">
                <div className="sd-doc-header sd-mb-4">
                    <div className="sd-badge sd-mb-2"><Server size={14} className="sd-inline-icon" /> Technologies</div>
                    <h1>Key <span className="sd-text-gradient">Technologies</span></h1>
                    <p>Deep dives into the tools and databases that power modern distributed systems at scale.</p>
                </div>

                <div className="sd-doc-layout">
                    {/* Sidebar Navigation */}
                    <aside className="sd-doc-sidebar">
                        <nav className="sd-doc-nav">
                            {Object.entries(groupedTechnologies).map(([category, items]) => (
                                <div key={category} className="sd-doc-nav-group">
                                    <h4 className="sd-doc-nav-title">
                                        {category}
                                        <span className="sd-topic-count">{items.length}</span>
                                    </h4>
                                    {items.map((tech) => (
                                        <button
                                            key={tech.id}
                                            className={`sd-doc-nav-link ${activeId === tech.id ? 'active' : ''}`}
                                            onClick={() => setActiveId(tech.id)}
                                        >
                                            {tech.title}
                                            {activeId === tech.id && <ChevronRight size={16} className="sd-active-icon" />}
                                        </button>
                                    ))}
                                </div>
                            ))}
                        </nav>
                    </aside>

                    {/* Content Area */}
                    <article className="sd-doc-content">
                        {/* Mobile Navigation Dropdown */}
                        <div className="sd-mobile-nav">
                            <div className="sd-mobile-nav-wrapper">
                                <select
                                    className="sd-mobile-nav-select"
                                    value={activeId}
                                    onChange={(e) => setActiveId(e.target.value)}
                                >
                                    {Object.entries(groupedTechnologies).map(([category, items]) => (
                                        <optgroup key={category} label={category}>
                                            {items.map(tech => (
                                                <option key={tech.id} value={tech.id}>
                                                    {tech.title}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="sd-prose">
                            <ReactMarkdown
                                components={{
                                    code({ node, inline, className, children, ...props }) {
                                        const match = /language-(\w+)/.exec(className || '');
                                        if (!inline && match && match[1] === 'mermaid') {
                                            return <Diagram chart={String(children).replace(/\n$/, '')} />;
                                        }
                                        return <code className={className} {...props}>{children}</code>;
                                    }
                                }}
                            >
                                {activeTech.content.replace(/<diagram>([\s\S]*?)<\/diagram>/g, (match, p1) => '\n```mermaid\n' + p1.trim() + '\n```\n')}
                            </ReactMarkdown>
                        </div>

                        {/* Next Topic */}
                        {nextTech && (
                            <div className="sd-next-topic" onClick={handleNextTopic}>
                                <div>
                                    <div className="sd-next-topic-label">Next Technology</div>
                                    <div className="sd-next-topic-title">{nextTech.title} <ArrowRight size={16} style={{ display: 'inline', verticalAlign: 'text-bottom' }} /></div>
                                </div>
                            </div>
                        )}
                    </article>
                </div>
            </div>
        </>
    );
};

export default Technologies;
